/**
 * 표 **전체 스케일**(우하단 코너 드래그)의 프리뷰/커밋 엔진 ProseMirror 플러그인.
 *
 * UI 핸들과 드래그 추적은 LumirTableHandlesController(에디터 DOM 밖 오버레이)가 담당한다.
 * 코너가 rowResizing(하단 경계)·columnResizing(우측 경계) 핸들과 겹쳐 mousedown이 그쪽으로
 * 새는 것을 피하기 위해, 핸들은 에디터 DOM 밖에 두고 자체 포인터 이벤트로 처리한다.
 * 이 플러그인은 컨트롤러가 dispatch한 프리뷰 상태를 받아:
 *  - 높이: 셀 `Decoration.node({style:height})` 라이브 프리뷰
 *  - 너비: 표 `<colgroup>` `<col>` width 직접 조작(BlockNoteTableView.ignoreMutation이 무시)
 * 로 그리고, 커밋 시 모든 셀의 colwidth/rowHeight를 일괄 setNodeMarkup(단일 undo)한다.
 * 갱신은 meta-only 트랜잭션이라 undo/onContentChange 스팸이 없다.
 *
 * ⚠️ prosemirror-* 는 @blocknote와 동일 인스턴스를 써야 한다(rowResizing와 동일 이유).
 */

import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorState } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import type { EditorView } from "prosemirror-view";
import { TableMap } from "prosemirror-tables";

export const tableScalingPluginKey = new PluginKey<TableScalePreview | null>(
  "lumirTableScaling",
);

export type TableScalePreview = {
  /** 대상 table 노드 pos. */
  tablePos: number;
  /** 드래그 시작 시점의 열 너비/행 높이(px) — materialize 값. */
  colWidths: number[];
  rowHeights: number[];
  /** 드래그 시작 시점의 표 렌더 W×H(px). */
  origW: number;
  origH: number;
  /** 현재 배율(≥1 또는 <1). */
  scale: number;
};

export function tableScaling(): Plugin<TableScalePreview | null> {
  return new Plugin<TableScalePreview | null>({
    key: tableScalingPluginKey,
    state: {
      init: () => null,
      apply(tr, prev) {
        const meta = tr.getMeta(tableScalingPluginKey);
        if (meta !== undefined) return meta.preview as TableScalePreview | null;
        if (prev && tr.docChanged) {
          return { ...prev, tablePos: tr.mapping.map(prev.tablePos, -1) };
        }
        return prev;
      },
    },
    props: {
      decorations(state) {
        const p = tableScalingPluginKey.getState(state);
        return p ? buildHeightDecorations(state, p) : null;
      },
    },
    view: (view) => ({
      update: () => {
        const p = tableScalingPluginKey.getState(view.state);
        if (p) applyColgroupPreview(view, p);
      },
    }),
  });
}

/** 컨트롤러가 호출: 프리뷰 상태 설정/해제(meta-only). */
export function setTableScalePreview(
  view: EditorView,
  preview: TableScalePreview | null,
): void {
  view.dispatch(view.state.tr.setMeta(tableScalingPluginKey, { preview }));
}

/** 컨트롤러가 호출: 드래그 시작 시 대상 표의 현재 열너비/행높이/렌더크기를 측정. */
export function measureTableForScale(
  view: EditorView,
  tablePos: number,
): TableScalePreview | null {
  const tableEl = findTableEl(view.nodeDOM(tablePos));
  const node = view.state.doc.nodeAt(tablePos);
  if (!tableEl || !node || node.type.name !== "table") return null;
  const map = TableMap.get(node);
  const rect = tableEl.getBoundingClientRect();
  const body = tableEl.tBodies[0];
  const rowHeights = body
    ? Array.from(body.rows).map((tr) => tr.getBoundingClientRect().height)
    : [];
  const colWidths = measureColWidths(tableEl, map.width);
  if (rowHeights.length !== map.height || colWidths.length !== map.width) {
    return null;
  }
  return {
    tablePos,
    colWidths,
    rowHeights,
    origW: rect.width,
    origH: rect.height,
    scale: 1,
  };
}

/** 컨트롤러가 호출: 드롭 시 모든 셀의 colwidth/rowHeight를 ×scale로 일괄 커밋(단일 undo). */
export function commitTableScale(
  view: EditorView,
  preview: TableScalePreview,
): void {
  const { state } = view;
  const { tablePos, colWidths, rowHeights, scale } = preview;
  const table = state.doc.nodeAt(tablePos);
  if (!table || table.type.name !== "table") return;
  const map = TableMap.get(table);
  const start = tablePos + 1;
  const tr = state.tr;
  const seen = new Set<number>();

  for (const relPos of map.map) {
    if (seen.has(relPos)) continue;
    seen.add(relPos);
    const node = table.nodeAt(relPos);
    if (!node) continue;
    const rect = map.findCell(relPos);
    const colwidth: number[] = [];
    for (let c = rect.left; c < rect.right; c++) {
      colwidth.push(Math.round((colWidths[c] ?? 0) * scale));
    }
    let h = 0;
    for (let r = rect.top; r < rect.bottom; r++) h += rowHeights[r] ?? 0;
    const rowHeight = Math.round(h * scale);
    tr.setNodeMarkup(start + relPos, undefined, {
      ...node.attrs,
      colwidth: colwidth.some((w) => w > 0) ? colwidth : null,
      rowHeight: rowHeight > 0 ? rowHeight : null,
    });
  }
  if (tr.docChanged) view.dispatch(tr);
}

// ── 측정 보조 ─────────────────────────────────────────────────
function measureColWidths(tableEl: HTMLTableElement, width: number): number[] {
  const widths = new Array(width).fill(0);
  const colgroup = tableEl.querySelector("colgroup");
  if (colgroup && colgroup.children.length === width) {
    let allSet = true;
    for (let i = 0; i < width; i++) {
      const w = parseFloat((colgroup.children[i] as HTMLElement).style.width);
      if (Number.isFinite(w) && w > 0) widths[i] = w;
      else allSet = false;
    }
    if (allSet) return widths;
  }
  const firstRow = tableEl.tBodies[0]?.rows[0];
  if (firstRow) {
    let col = 0;
    for (const cell of Array.from(firstRow.cells)) {
      const span = cell.colSpan || 1;
      const w = cell.getBoundingClientRect().width / span;
      for (let s = 0; s < span && col < width; s++) widths[col++] = w;
    }
  }
  return widths;
}

// ── 프리뷰 렌더 ────────────────────────────────────────────────
function applyColgroupPreview(view: EditorView, p: TableScalePreview) {
  const tableEl = findTableEl(view.nodeDOM(p.tablePos));
  const colgroup = tableEl?.querySelector("colgroup");
  if (!colgroup) return;
  const cols = colgroup.children;
  for (let i = 0; i < cols.length && i < p.colWidths.length; i++) {
    (cols[i] as HTMLElement).style.width =
      Math.round(p.colWidths[i] * p.scale) + "px";
  }
}

function buildHeightDecorations(
  state: EditorState,
  p: TableScalePreview,
): DecorationSet {
  const table = state.doc.nodeAt(p.tablePos);
  if (!table || table.type.name !== "table") return DecorationSet.empty;
  const map = TableMap.get(table);
  const start = p.tablePos + 1;
  const decorations: Decoration[] = [];
  const seen = new Set<number>();
  for (const relPos of map.map) {
    if (seen.has(relPos)) continue;
    seen.add(relPos);
    const node = table.nodeAt(relPos);
    if (!node) continue;
    const rect = map.findCell(relPos);
    let h = 0;
    for (let r = rect.top; r < rect.bottom; r++) h += p.rowHeights[r] ?? 0;
    const from = start + relPos;
    const to = from + node.nodeSize;
    decorations.push(
      Decoration.node(from, to, {
        class: "lumir-table-scale-dragging",
        style: `height: ${Math.round(h * p.scale)}px`,
      }),
    );
  }
  return DecorationSet.create(state.doc, decorations);
}

/** 임의 DOM에서 가장 가까운 <table>을 찾는다(nodeDOM이 wrapper일 수 있음). */
function findTableEl(dom: Node | null): HTMLTableElement | null {
  if (!dom) return null;
  const el = dom as HTMLElement;
  if (el.nodeName === "TABLE") return el as HTMLTableElement;
  const inner = el.querySelector?.("table");
  if (inner) return inner as HTMLTableElement;
  return (el.closest?.("table") as HTMLTableElement) ?? null;
}
